import { Component, Pipe, PipeTransform, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { describeError } from '@cf-shared/error-describe';
import { untranslatedString } from 'models/translated-string';
import { CsPage, NavController } from 'providers/nav-controller';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { StressLevel, SleepQuality, Gender } from '@cf-shared/enums';
import { TrainingLevel } from '@cf-shared/data-model/training-level';
import { WorkoutSolverParams, WorkoutSolverExercise, ExerciseId, WorkoutSolverConstants, WorkoutSolverOutcome } from '@cf-shared/workout-solver-interface';
import { requireWorkoutProgram } from 'services/server/workout-solver-failure';
import {
  getSimpleMuscleGrowthPreferenceTable,
  OneMuscleGrowthValue,
} from '@cf-shared/data-model/muscle-growth-preference';
import { SettingsService } from 'services/settings.service';
import { deepNormalize, deepClone } from '@cf-shared/deep-helpers';
import {
  buildSolverParams,
  createDefaultSolverTestParams,
  fillStoredTestCaseDefaults,
} from './test-workout-solver-params';
import { AuthService } from 'services/auth.service';
import { AuthRole } from '@cf-shared/couch-auth';
import { uniq, sumBy, flatMap } from 'lodash-es';
import { TestCasesDbService, WorkoutTestCase } from 'services/tests-cases-db.service';
import { DateTime } from 'luxon';
import * as crypto from 'crypto';
import { DatabaseWrapper } from 'services/database-wrapper';
import { PopupMenuProvider, PopupMenuOptions } from 'providers/popup-menu/popup-menu';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { FitnessPlan, Plan_SubSlot, Plan_Workout } from '@cf-shared/data-model/fitness-plan';
import { LoadingController } from 'providers/loading-controller';

@Pipe({
    name: 'dayExercises', pure: false,
    standalone: false
})
export class DayExercisesPipe implements PipeTransform {
  transform(day: WorkoutSolverExercise[]): string {
    return sumBy(day, exercise => (exercise.sets > 0 ? 1 : 0)).toFixed(2);
  }
}
@Pipe({
    name: 'daySets', pure: false,
    standalone: false
})
export class DaySetsPipe implements PipeTransform {
  transform(day: WorkoutSolverExercise[]): string {
    return sumBy(day, 'sets').toFixed(2);
  }
}

@Pipe({
    name: 'evaluateWorkout', pure: false,
    standalone: false
})
export class EvaluateWorkoutPipe implements PipeTransform {
  transform(
    workout: WorkoutSolverExercise[][],
    opts: { loopsPerWeek: number; constants: WorkoutSolverConstants; originalWorkout: WorkoutSolverExercise[][] },
  ): string {
    let text = '';
    if (!opts.constants) {
      return text;
    }

    // verify muscle volume
    for (let muscleName in opts.constants.muscles) {
      let sets = 0;
      let orgSets = 0;
      for (let day of workout) {
        for (let exercise of day) {
          if (exercise.subSlotId && exercise.sets) {
            sets += (opts.constants.exercises[exercise.subSlotId].muscleFactors[muscleName] || 0) * exercise.sets * opts.loopsPerWeek;
          }
        }
      }

      for (let day of opts.originalWorkout) {
        for (let exercise of day) {
          orgSets += (opts.constants.exercises[exercise.subSlotId].muscleFactors[muscleName] || 0) * exercise.sets * opts.loopsPerWeek;
        }
      }

      text += `${muscleName}: ${sets.toFixed(2)} vs ${orgSets.toFixed(2)} sets/wk\n`;
    }
    return text;
  }
}

@Pipe({
    name: 'evaluateExercise', pure: false,
    standalone: false
})
export class EvaluateExercisePipe implements PipeTransform {
  transform(customExercise: WorkoutSolverExercise, opts: { constants: WorkoutSolverConstants }): string {
    let text = '';
    if (!opts.constants || !opts.constants.exercises[customExercise.subSlotId]) {
      return text;
    }

    for (let muscleName in opts.constants.muscles) {
      let muscleFactor = opts.constants.exercises[customExercise.subSlotId].muscleFactors[muscleName] || 0;
      let sets = muscleFactor * customExercise.sets;
      if (sets > 0) text += `${muscleName}:${sets.toFixed(2)}  `;
    }
    return text;
  }
}

@Pipe({
    name: 'evaluateDay', pure: false,
    standalone: false
})
export class EvaluateDayPipe implements PipeTransform {
  transform(
    dayNumber: number,
    opts: { constants: WorkoutSolverConstants; customWorkout: WorkoutSolverExercise[][]; originalWorkout: WorkoutSolverExercise[][] },
  ): string {
    let text = '';
    if (!opts.constants || !opts.customWorkout || !opts.originalWorkout) {
      return text;
    }

    let originalDay = opts.originalWorkout[dayNumber];
    let customDay = opts.customWorkout[dayNumber];

    // verify muscle volume
    for (let muscleName in opts.constants.muscles) {
      let sets = 0;
      let orgSets = 0;
      for (let exercise of customDay) {
        if (exercise.subSlotId && exercise.sets) {
          sets += (opts.constants.exercises[exercise.subSlotId].muscleFactors[muscleName] || 0) * exercise.sets;
        }
      }

      for (let exercise of originalDay) {
        orgSets += (opts.constants.exercises[exercise.subSlotId].muscleFactors[muscleName] || 0) * exercise.sets;
      }

      text += `${muscleName}: ${sets.toFixed(2)} vs ${orgSets.toFixed(2)}\n`;
    }
    return text;
  }
}

@Component({
    selector: 'page-test-workout-solver',
    templateUrl: 'test-workout-solver.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TestWorkoutSolverPage extends CsPage<{}> {
  allTests: WorkoutTestCase[];
  currentTest: WorkoutTestCase = null;

  resultDays: WorkoutSolverExercise[][] = [];
  fitnessPlan: string;
  customDays: WorkoutSolverExercise[][] = [];
  exerciseIds: ExerciseId[] = [];

  testSolutionStatus: 'failed' | 'warning_passed' | 'passed' | null = null;
  testPassed: boolean | null = null;
  testPassedMessage: string;
  approvalText: string;
  approvalState: 'approved' | 'failed' | 'none' = 'none';
  solvedToApprovedDiff: {
    solved?: any;
    approved?: any;
    diff?: number[][];
    sameExercises?: boolean;
  } = null;

  params = createDefaultSolverTestParams();
  // needed to access enums in html
  StressLevel = StressLevel;
  TrainingLevel = TrainingLevel;
  SleepQuality = SleepQuality;
  Gender = Gender;
  debug: string;
  constantsText: string;
  constants: WorkoutSolverConstants;
  loopsPerWeek = 0;
  showConstants = false;
  showComparisons = false;

  constructor(
    public navCtrl: NavController,
    public settings: SettingsService,
    public cloudFunctionsService: CloudFunctionsService,
    public loadingCtrl: LoadingController,
    private alertCtrl: AlertController,
    private popupCtrl: PopupMenuProvider,
    protected auth: AuthService,
    private testCasesDb: TestCasesDbService,
    private changeDetector: ChangeDetectorRef,
  ) {
    super();
  }

  // creates an empty exercise for adding to test days
  createEmptyExercise(): WorkoutSolverExercise {
    return { subSlotId: '' as ExerciseId, trainingLevel: TrainingLevel.Novice, sets: 0, intensities: {} };
  }

  async ionViewCanEnter() {
    await this.auth.pageAuthGuardAsync(AuthRole.Dev);
    await this.testCasesDb.initializeAsync();
    await this.reloadAllTestsAsync(this.settings.currentWOSolverTestId);
  }

  initiateSolving() {
    setTimeout(() => this.solveAsync());
  }

  async solveAsync(): Promise<void> {
    if (!this.settings.isLoaded()) {
      // this happens when refreshing page during debugging.
      // we must retutn here, or the save below would reset settings to default!
      return;
    }

    let loading = this.loadingCtrl.create({
      content: untranslatedString('Generating training program...'),
    });

    void loading.present();
    let startTime = window.performance.now();

    deepNormalize(this.params.muscleGrowth, getSimpleMuscleGrowthPreferenceTable(OneMuscleGrowthValue.Maximize));

    // `this.params` IS the test being solved on this path, so handing it over whole is
    // what gives this solve the open test case's own parameters
    let params: WorkoutSolverParams = buildSolverParams(this.params);

    this.debug = '';
    // UI can only set "", not null - so convert here. This runs AFTER the request was
    // assembled and still reaches it, because the request shares this very map rather
    // than a copy of it
    if (typeof this.params.trainingLevels == 'object') {
      for (let key of Object.keys(this.params.trainingLevels)) {
        if (!this.params.trainingLevels[key]) {
          this.params.trainingLevels[key] = undefined;
        }
      }
    }

    try {
      if (this.showConstants && this.constantsText && this.constantsText.length > 3) {
        params.constants = JSON.parse(this.constantsText);
      } else {
        params.constants = undefined;
      }

      // the first solve's volume feeds the second, so it is checked first: reading it off a failed
      // reply used to send "not a number" into the second solve and hide the real error (#5942)
      const firstResult = requireWorkoutProgram(await this.cloudFunctionsService.solveWorkoutsAsync(params), 'solveWorkouts');
      params.declaredState.trainingVolumeWeeklyAvg = Math.round(firstResult.trainingVolumeWeeklyAvg);
      const result = requireWorkoutProgram(await this.cloudFunctionsService.solveWorkoutsAsync(params), 'solveWorkouts');

      this.resultDays = result.days;
      this.customDays = deepClone(result.days);
      if (result.constantsText) {
        this.constants = JSON.parse(result.constantsText);
        this.exerciseIds = Object.keys(this.constants.exercises) as ExerciseId[]; // exerciseid is effectively a string
        this.loopsPerWeek = sumBy(params.declaredState.trainingDays, d => (d ? 1 : 0)) / result.days.length;
      }

      this.debug = result.debug;
      this.constantsText = result.constantsText;

      let workouts: Plan_Workout[] = [];
      let subslots: {
        [subSlotId: string]: Plan_SubSlot;
      } = {};
      let workoutLabels = ['A', 'B', 'C', 'D', 'E', 'F', 'G']
      for (let iDay = 0; iDay < this.resultDays.length; ++iDay) {
        let subslotsIds: string[] = [];
        for (let exercise of this.resultDays[iDay]) {
          let subslotId: string = exercise.subSlotId;
          subslotsIds.push(subslotId);
          subslots[subslotId] = {
            slotId: subslotId,
            startIndex: 0,
            exercise_id: '',
            trainingLevel: 300,
            intensities: exercise.intensities,
            defaultVolume_sets: exercise.sets,
            exercisePaths: [],
            rebuildPathsForIntensity: 100,
          }
        }
        workouts.push({
          name: workoutLabels[iDay],
          subSlotIds: subslotsIds,
        });
      }
      let fitnessPlan: Partial<FitnessPlan> = {
        trainingLevels: result.trainingLevels,
        workouts: workouts,
        subSlots: subslots,
      }

      this.fitnessPlan = JSON.stringify(fitnessPlan, null, 2);

      let endTime = window.performance.now();
      this.debug += `\nDone in ${(endTime - startTime) / 1000} seconds\n`;

      this.recheckApproval();

      void loading.dismiss();
    } catch (error) {
      void loading.dismiss();
      console.log(`Solver error: ${error}\n`);
      alert(error);
    }
  }

  protected originalSetsText(dayIndex: number, subSlotId: string) {
    let originalExercise = this.resultDays[dayIndex].find(e => e.subSlotId === subSlotId);
    let customExercise = this.customDays[dayIndex].find(e => e.subSlotId === subSlotId);
    let originalSets = originalExercise ? originalExercise.sets : 0;
    let customSets = customExercise ? customExercise.sets : 0;
    if (customSets === originalSets) {
      return '';
    } else {
      return `vs ${originalSets}`;
    }
  }

  private generateTestId(test: WorkoutTestCase) {
    let userid = this.auth.user.uid;
    let time = DateTime.utc().toISO();

    let hashSource = userid + ':' + time;

    let testHashSecret = 'x6xx4lMtbmESu6vhmFkM66aYUlqyqmDQ';

    let hash = crypto.createHmac('SHA1', testHashSecret).update(hashSource).digest('hex');
    test._id = `test/workout/${hash}`;
  }

  async reloadAllTestsAsync(currentTestId?: string) {
    console.log('reloading started');

    let oldId = (this.currentTest && this.currentTest._id) || currentTestId;
    this.allTests = await this.testCasesDb.getAllWorkoutTestCasesAsync();
    this.allTests.sort((a, b) => a.name.localeCompare(b.name));
    this.selectAnyTest(oldId);
    this.testCaseToUI();
    this.changeDetector.markForCheck();
    console.log('reloading done');
  }

  testCaseToUI() {
    if (!this.currentTest || !this.currentTest.testWorkoutSolverJSON) {
      console.error('No data to load test case');
      return;
    }
    // the missing fields are filled from the fixed defaults, NOT from `this.params`: by now
    // that holds whichever test case was selected before this one, so normalising against it
    // filled a stored case's missing fields with the previous case's values (df-a74d50783aa1)
    this.params = fillStoredTestCaseDefaults(JSON.parse(this.currentTest.testWorkoutSolverJSON));

    this.settings.currentWOSolverTestId = this.currentTest._id;
    this.settings.saveInBackground('workout-solver test selection');

    this.recheckApproval();
  }
  testCaseFromUI() {
    if (!this.currentTest) {
      console.error('No test case to save data');
      return;
    }
    let json = JSON.stringify(this.params);
    this.currentTest.testWorkoutSolverJSON = json;
  }

  selectAnyTest(id?: string) {
    if (this.currentTest && this.allTests.some(t => t === this.currentTest)) {
      return;
    }
    this.currentTest = this.allTests.find(t => t._id === id);

    // in case the required is not found, select first one
    if (!this.currentTest && this.allTests.length > 0) {
      this.currentTest = this.allTests[0];
    }
  }

  async onSelectTestCaseAsync() {
    await this.onCurrentTestChangedAsync();
    this.testCaseToUI();
  }

  async askTestNameAsync(title: string, test: WorkoutTestCase): Promise<boolean> {
    return this.alertCtrl.showAlertAsync<boolean>({
      enableBackdropDismiss: true,
      title: untranslatedString(title),
      buttons: [
        {
          text: untranslatedString('OK'),
          role: AlertButtonRole.Primary,
          result: (data: { name: string }) => {
            test.name = data.name;
            return true;
          },
        },
        { text: untranslatedString('Cancel'), role: AlertButtonRole.Cancel, result: false },
      ],
      inputs: [{ type: 'text', name: 'name', value: test.name }],
    });
  }

  async onPrevNextTestAsync(direction: 1 | -1) {
    let numTests = this.allTests.length;
    if (numTests <= 0) {
      return;
    }
    let testIndex = this.allTests.findIndex(t => t === this.currentTest);
    testIndex = (testIndex + numTests /* adding numTests to avoid handling modulos of negative numbers */ + direction) % numTests;
    this.currentTest = this.allTests[testIndex];

    await this.onCurrentTestChangedAsync();
  }

  async onCurrentTestChangedAsync() {
    this.resultDays = [];
    this.customDays = [];
    this.debug = null;
    this.testCaseToUI();
  }
  canPrevNextTest(direction: 1 | -1): boolean {
    let numTests = this.allTests.length;
    if (numTests <= 1) {
      return false;
    }
    let testIndex = this.allTests.findIndex(t => t === this.currentTest);
    if (testIndex < 0) {
      return true; // in weird case current test doesn't exist, allow next/prev to auto-recover
    }
    testIndex += direction;
    return testIndex >= 0 && testIndex < numTests;
  }

  async onSaveTestAsync() {
    let test = this.currentTest;
    if (test) {
      let currentTestLocalParamsJSON = JSON.stringify(this.params);
      let currentTestCloudParamsJSON = test.testWorkoutSolverJSON;
      // If solved and already saved test-case params are changed but never resolved error should popup and saving can't continue.
      if (
        test.approvedResultDays != null &&
        currentTestCloudParamsJSON != null &&
        currentTestCloudParamsJSON != currentTestLocalParamsJSON &&
        this.testPassed == null
      ) {
        await this.alertCtrl.showAlertAsync({
          title: untranslatedString('Saving warning'),
          subTitle: untranslatedString(`Test-case ${test.name} params are changed. Please resolve test before saving it.`),
          buttons: [{ text: untranslatedString('Ok'), role: AlertButtonRole.Primary }],
        });

        return;
      } else {
        // If test has failed after solving then on saving aprovals should be removed.
        if (this.testSolutionStatus != null && this.testSolutionStatus != 'passed' && test.approvedBy != null) {
          let alertResult = await this.alertCtrl.showAlertAsync<boolean>({
            enableBackdropDismiss: true,
            title: untranslatedString(
              'Solution for this test has some problems, please check them before proceeding. Saving this test will remove any existing approval. Do you want to continue?'
            ),
            buttons: [
              { text: untranslatedString('Yes'), role: AlertButtonRole.Primary, result: true },
              { text: untranslatedString('No'), role: AlertButtonRole.Cancel, result: false },
            ],
          });

          if (alertResult == true) {
            test.approvedBy = null;
            test.approvedResultDays = null;
          } else {
            return;
          }
        }
        console.log('saving started');
        this.testCaseFromUI();
        await this.testCasesDb.saveWorkoutTestCaseAsync(test, DatabaseWrapper.getTransactionId());
        console.log('saved');
      }
    }
    await this.reloadAllTestsAsync();
    console.log('saving done');
  }
  async onRenameTestAsync() {
    let test = this.currentTest;
    if (test) {
      let confirmed = await this.askTestNameAsync('Rename this test', test);
      if (confirmed) {
        await this.onSaveTestAsync();
      }
    }
  }
  async onAddTestAsync() {
    let test = new WorkoutTestCase();
    test.name = 'Unnamed';
    this.generateTestId(test);
    let confirmed = await this.askTestNameAsync('Name for the new test', test);
    if (confirmed) {
      this.allTests.push(test);
      this.currentTest = test;
      this.testCaseFromUI();
      await this.onSaveTestAsync();
    }
    console.log('adding done');
  }
  async onDeleteTestAsync() {
    let test = this.currentTest;
    if (!test) {
      return;
    }

    let doDelete = await this.alertCtrl.showAlertAsync<boolean>({
      title: untranslatedString(`Are you sure you want to delete '${test.name}'`),
      buttons: [
        { text: untranslatedString('Delete'), role: AlertButtonRole.Primary, result: true },
        { text: untranslatedString('Cancel'), role: AlertButtonRole.Cancel, result: false },
      ],
    });
    if (doDelete) {
      await this.testCasesDb.deleteWorkoutTestCaseAsync(test, DatabaseWrapper.getTransactionId());
      await this.reloadAllTestsAsync();
    }
  }

  compareSolutionToApproved(
    solution: WorkoutSolverExercise[][],
    approved: WorkoutSolverExercise[][],
  ): { same: boolean; sameExercises: boolean; diff: number[][] } {
    if (!solution || !approved) {
      return { same: false, sameExercises: false, diff: null };
    }

    let compareResult = true;
    if (solution.length !== approved.length) {
      compareResult = false;
    }
    let maxDays = Math.max(solution.length, approved.length);
    let solutionDiffs: number[][] = [];
    for (let dayIndex = 0; dayIndex < maxDays; dayIndex++) {
      let dayDiff: number[] = [];
      solutionDiffs.push(dayDiff);
      if (dayIndex >= solution.length && dayIndex < approved.length) {
        for (let i = 0, ct = approved[dayIndex].length; i < ct; ++i) {
          dayDiff.push(2);
        }
      } else if (dayIndex < solution.length && dayIndex >= approved.length) {
        for (let i = 0, ct = solution[dayIndex].length; i < ct; ++i) {
          dayDiff.push(1);
        }
      }

      let solutionDay = solution[dayIndex] || [];
      let approvedDay = approved[dayIndex] || [];
      let maxExercisesPerDay = Math.max(solutionDay.length, approvedDay.length);
      if (solutionDay.length !== approvedDay.length) {
        compareResult = false;
      }

      for (let exerciseIndex = 0; exerciseIndex < maxExercisesPerDay; exerciseIndex++) {
        dayDiff.push(-1);
        // If there is more excersies in approved solution day mark diff as shortage.
        if (exerciseIndex >= solutionDay.length && exerciseIndex < approvedDay.length) {
          dayDiff[exerciseIndex] = 2;
          continue;
          // If there is more excersies in approved solution day mark diff as surplus.
        } else if (exerciseIndex < solutionDay.length && exerciseIndex >= approvedDay.length) {
          dayDiff[exerciseIndex] = 1;
          continue;
        }
        let solutionExercise = solutionDay[exerciseIndex];
        let approvedExercise = approvedDay[exerciseIndex];
        if (solutionExercise.subSlotId !== approvedExercise.subSlotId || solutionExercise.sets !== approvedExercise.sets) {
          compareResult = false;
          // Mark as different.
          dayDiff[exerciseIndex] = 0;
        }
      }
    }

    if (compareResult == false) {
      let exerciseComparator = (e1: WorkoutSolverExercise, e2: WorkoutSolverExercise) => {
        if (e1.subSlotId < e2.subSlotId) {
          return -1;
        } else if (e1.subSlotId > e2.subSlotId) {
          return +1;
        } else {
          return e1.sets - e2.sets;
        }
      };
      let solutionExercises = flatMap(solution).sort(exerciseComparator);
      let approvedExercises = flatMap(approved).sort(exerciseComparator);
      let sameExercises = false;
      if (solutionExercises.length === approvedExercises.length) {
        sameExercises = true;
        for (let i = 0; i < solutionExercises.length; i++) {
          if (exerciseComparator(solutionExercises[i], approvedExercises[i]) !== 0) {
            sameExercises = false;
            break;
          }
        }
      }

      return { same: false, sameExercises, diff: solutionDiffs };
    }

    return { same: true, sameExercises: true, diff: solutionDiffs };
  }

  recheckApproval() {
    this.testPassed = null;
    this.testSolutionStatus = null;
    this.solvedToApprovedDiff = null;

    if (this.currentTest.approvedBy && this.currentTest.approvedBy.length > 0) {
      this.approvalText = this.currentTest.approvedBy.join(', ');
    } else {
      this.approvalText = null;
    }

    if (!this.currentTest.approvedResultDays || this.currentTest.approvedResultDays.length <= 0) {
      this.approvalState = 'none';
      return;
    }
    // this makes sure that approved tests are rendered as "ok" until a fresh solution is actually generated
    if (!this.resultDays || this.resultDays.length <= 0) {
      this.approvalState = 'approved';
      return;
    }

    let {
      same: compareResult,
      sameExercises,
      diff: compareDiff,
    } = this.compareSolutionToApproved(this.resultDays, this.currentTest.approvedResultDays);
    if (compareResult) {
      this.testPassed = true;
      this.testSolutionStatus = 'passed';
      this.testPassedMessage = 'Result is ok';
    } else {
      this.solvedToApprovedDiff = {
        solved: this.resultDays,
        approved: this.currentTest.approvedResultDays,
        diff: compareDiff,
        sameExercises: sameExercises,
      };
      let currentTestLocalParamsJSON = JSON.stringify(this.params);
      let currentTestCloudParamsJSON = this.currentTest.testWorkoutSolverJSON;
      if (currentTestCloudParamsJSON == currentTestLocalParamsJSON) {
        this.testSolutionStatus = 'failed';
        this.testPassedMessage = 'This solution is different than one stored in the cloud even though the parameters are the same.';
        this.testPassed = false;
      } else {
        this.testPassed = true;
        this.testSolutionStatus = 'warning_passed';
        this.testPassedMessage = 'The parameters and solution has changed for this test. Reapproval is needed.';
      }
    }
  }

  async checkOneTestAsync(test: WorkoutTestCase) {
    let resultInfo: any = {};
    resultInfo.name = test.name;
    // the parse sits inside the try on purpose: a stored case that is not valid JSON, or not
    // an object, becomes this one test's failed row instead of rejecting the whole recheck
    // and leaving its spinner up (df-306e075759a5)
    try {
      // fill in whatever this stored test case was saved before, keeping a training level it
      // deliberately left Undefined
      let testParams = fillStoredTestCaseDefaults(JSON.parse(test.testWorkoutSolverJSON));

      // every field comes off the test case we were handed, the muscle-growth pair included.
      // Reading those two off `this.params` meant "Recheck all tests" solved every stored
      // test with the SELECTED test's preference (df-a74d50783aa1)
      let params: WorkoutSolverParams = buildSolverParams(testParams);

      let solverResult = await this.cloudFunctionsService.solveWorkoutsAsync(params);
      // if there is no program, say why and stop; comparing a missing program to the approved
      // one used to overwrite this message with "is different" (#5942)
      if (solverResult.outcome !== WorkoutSolverOutcome.Solved) {
        resultInfo.message = `Test ${test.name} solved with error.`;
        resultInfo.error = solverResult.error;
        return resultInfo;
      }

      if (test.approvedResultDays) {
        if (this.compareSolutionToApproved(solverResult.days, test.approvedResultDays).same) {
          resultInfo.status = 'passed';
        } else {
          resultInfo.message = `Test ${test.name} is different.`;
        }
      } else {
        resultInfo.message = `Test ${test.name} results does not exists in database.`;
      }
    } catch (e) {
      // developer page: the row shows the failure, with the solver's own reason
      resultInfo.message = `Test ${test.name} could not be solved: ${describeError(e)}`;
    }

    return resultInfo;
  }

  handleRecheckAllTests() {
    void this.recheckAllTestsAsync();
  }
  async recheckAllTestsAsync() {
    let loading = this.loadingCtrl.create({
      content: untranslatedString('Rechecking tests...'),
    });

    void loading.present();

    // the dismiss is in a finally so that nothing thrown while checking can leave the modal
    // spinner up with no way to clear it (df-306e075759a5)
    let results: any[];
    try {
      results = await Promise.all(this.allTests.map(async test => this.checkOneTestAsync(test)));
    } finally {
      await loading.dismiss();
    }

    let passedSolutionsCount = 0;
    let debugText = '';
    for (let testResult of results) {
      // `status` is only ever set to 'passed' today, so this counts the same rows as the
      // `!= null` test it replaces — but it says what it means, and it will not count a
      // future 'failed' status as a pass
      if (testResult.status === 'passed') {
        ++passedSolutionsCount;
        continue;
      }

      debugText += `${testResult.message}<br>`;
      if (testResult.error) {
        console.log(`Test ${testResult.name} solving error: ${testResult.error}`);
      }
    }
    debugText = `${passedSolutionsCount} solutions passed.<br>${debugText}`;

    await this.alertCtrl.showAlertAsync({
      title: untranslatedString('Testing results info'),
      message: untranslatedString(debugText),
      buttons: [{ text: untranslatedString('Ok'), role: AlertButtonRole.Primary }],
    });
  }

  handleTestPassedInfoButtonPressed() {
    void this.alertCtrl.showAlertAsync({
      title: untranslatedString('Test result info'),
      subTitle: untranslatedString(this.testPassedMessage),
      buttons: [{ text: untranslatedString('Dismiss'), role: AlertButtonRole.Primary }],
    });
  }

  async askApprovalAsync(approved: boolean) {
    let test = this.currentTest;
    if (!test) {
      return;
    }

    await this.alertCtrl.showAlertAsync({
      enableBackdropDismiss: true,
      title: untranslatedString(approved
        ? `Are you sure you approve of these results for '${test.name}'`
        : `Are you sure you want to reject solution for '${test.name}' approved by ${test.approvedBy.join(', ')}?`),
      buttons: [
        {
          text: untranslatedString(approved ? 'Yes, I approve' : 'I reject this solution'),
          role: AlertButtonRole.Primary,
          result: () => {
            void (async () => {
              await this.doApproveResultsAsync(approved);
            })();
          },
        },
        { text: untranslatedString('Cancel'), role: AlertButtonRole.Cancel },
      ],
    });
  }

  async doApproveResultsAsync(approved: boolean) {
    if (approved) {
      if (this.approvalState !== 'approved') {
        this.currentTest.approvedResultDays = deepClone(this.resultDays);
        this.currentTest.approvedBy = [];
      }
      this.currentTest.approvedBy.push(this.auth.user.name);
      this.currentTest.approvedBy = uniq(this.currentTest.approvedBy);
    } else {
      this.currentTest.approvedBy = this.currentTest.approvedBy.filter(e => e != this.auth.user.name);
      if (this.currentTest.approvedBy.length == 0) {
        this.currentTest.approvedBy = null;
        this.currentTest.approvedResultDays = null;
      }
    }
    this.recheckApproval();
    await this.onSaveTestAsync();
  }

  async onApprovalMenuAsync(ev: Event) {
    let opts: PopupMenuOptions = { buttons: [], targetFromElement: ev.target as HTMLElement };

    let alereadyApprovedByUser = this.currentTest.approvedBy != null && this.currentTest.approvedBy.find(e => e == this.auth.user.name);
    if (!alereadyApprovedByUser) {
      opts.buttons.push({
        text: untranslatedString('Approve'),
        icon: 'checkmark',
        handler: () => this.askApprovalAsync(true),
      });
    } else {
      opts.buttons.push({
        text: untranslatedString('Unapprove'),
        icon: 'close',
        handler: () => this.askApprovalAsync(false),
      });
    }
    void this.popupCtrl.createMenu(opts);
  }
}
